import { useMemo } from "react";
import { getPortfolioReportingSummaryQuery } from "web_myplann/Api/Portfolio/Queries/getPortfolioReportingSummaryQuery";
import { usePortfolioListQuery } from "web_myplann/Api/Portfolio/Queries/usePortfolioListQuery";
import { usePropertyListQuery } from "web_myplann/Api/Portfolio/Queries/usePropertyListQuery";
import type { PortfolioListReportingSummaryResponseDTO } from "web_myplann/Infrastructure/Api/Api";
import { PORTFOLIO_SORT } from "web_myplann/Portfolio/Components/Redesign/Filter/portfolioSortKey";
import { getReportingPortfolioIds } from "web_myplann/Portfolio/Helpers/portfolioReportingDomains";
import { useEffectivePortfolioFilter } from "web_myplann/Portfolio/Hooks/useEffectivePortfolioFilter";
import { usePortfolioCategories } from "web_myplann/Portfolio/Hooks/usePortfolioCategories";
import type { PortfolioCategory } from "web_myplann/Portfolio/Types/portfolioCategory";
import type { PortfolioFilterState } from "web_myplann/Shared/State/portfolioFilterAtom";
import { sumGroupItems } from "web_myplann/Shared/Utils/sumGroupItems";

type PortfolioListData = {
	categories: PortfolioCategory[];
	filter: PortfolioFilterState;
	totalItems: number;
	summaryData: PortfolioListReportingSummaryResponseDTO[] | undefined;
	isLoading: boolean;
	hasNoData: boolean;
};

export const usePortfolioListData = (): PortfolioListData => {
	const { filter } = useEffectivePortfolioFilter();

	const {
		data: portfolioList,
		isLoading: isPortfolioLoading,
		isSuccess: isPortfolioSuccess,
	} = usePortfolioListQuery();
	const {
		data: propertyList,
		isLoading: isPropertyLoading,
		isSuccess: isPropertySuccess,
	} = usePropertyListQuery();

	const portfolioItems = useMemo(
		() => portfolioList?.data ?? [],
		[portfolioList?.data],
	);
	const propertyItems = useMemo(
		() => propertyList?.data ?? [],
		[propertyList?.data],
	);

	const reportingIds = useMemo(
		() => getReportingPortfolioIds(portfolioItems),
		[portfolioItems],
	);
	const { data: reportingSummary } =
		getPortfolioReportingSummaryQuery(reportingIds);
	const summaryData = reportingSummary?.data;

	const selectedCategories = useMemo(
		() =>
			filter.categories.length > 0 ? new Set(filter.categories) : undefined,
		[filter.categories],
	);

	const isOblastSort = filter.sort === PORTFOLIO_SORT.oblast;

	const sortedCategories = usePortfolioCategories(
		portfolioItems,
		propertyItems,
		{
			categories: selectedCategories,
			sort: filter.sort,
			sortDesc: isOblastSort ? false : filter.sortDesc,
			summaryData,
		},
	);

	const categories = useMemo(
		() =>
			isOblastSort && filter.sortDesc
				? [...sortedCategories].reverse()
				: sortedCategories,
		[sortedCategories, isOblastSort, filter.sortDesc],
	);

	const totalItems = useMemo(() => sumGroupItems(categories), [categories]);

	const isLoading = isPortfolioLoading || isPropertyLoading;

	const hasNoData =
		isPortfolioSuccess &&
		isPropertySuccess &&
		portfolioItems.length === 0 &&
		propertyItems.length === 0;

	return {
		categories,
		filter,
		totalItems,
		summaryData,
		isLoading,
		hasNoData,
	};
};
